import { useAsync } from "@coideo/barrani";
import { FullPageLoader } from "lib";
import posthog from "posthog-js";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as auth from "utils/auth-client";
import * as cashDrawer from "utils/cash-drawer-client";
import { getStoredItem, removeStoredItem, saveStoredItem } from "utils/StorageItem";

import { useShop } from "./shop-context";

export const backoffice: auth.POS = {
  id: "BACKOFFICE",
  name: "Backoffice",
  noServiceCharge: false,
  cashDrawers: [],
};

const posKey = (user: auth.User) => `__web_pos_selected_pos_${user.id}__`;
const sessionKey = (user: auth.User, pos: auth.POS) =>
  `__web_pos_session_id_${user.id}_${pos.id}__`;

async function getData() {
  const user = await auth.getUser();

  if (!user) return null;

  const pos = getStoredItem(posKey(user));
  const posData =
    pos === "BACKOFFICE" ? backoffice : user.pos.find((p) => p.id.toString() === pos) ?? null;

  return { user, posData, sessionId: !posData ? null : getStoredItem(sessionKey(user, posData)) };
}

type Auth = {
  user: auth.User | null;
  hasPermissions: (permissions: auth.PERMISSIONS[]) => boolean;
  isBackoffice: boolean;
  login: (data: auth.LoginData) => Promise<void>;
  logout: () => void;
  posData: auth.POS | null;
  setPos: (value: auth.POS | null) => void;
  sessionId: string | null;
  setSession: (value: string | null) => void;
  openSession: (data: { name: string; initialAmount: number }) => Promise<void>;
  closeSession: (data: { closedDrawerAmount: number; sessionId: string }) => Promise<void>;
  openCashDrawer: boolean;
  setOpenCashDrawer: (value: boolean) => void;
};

const AuthContext = createContext<Auth | undefined>(undefined);

AuthContext.displayName = "AuthContext";

const AuthProvider = (props: { children: ReactNode }) => {
  const { data, status, error, isLoading, isIdle, isError, isSuccess, run, setData } = useAsync<{
    user: auth.User;
    posData: auth.POS | null;
    sessionId: string | null;
  }>();
  const { url, id, name } = useShop();
  const [openCashDrawer, setOpenCashDrawer] = useState(false);

  useEffect(() => {
    const dataPromise = getData();

    void run(dataPromise).then((data) => {
      if (data?.user) {
        posthog.identify(data.user.id.toString(), {
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
        });
        posthog.group(url, id.toString(), { id, name });
      }
    });
  }, [id, name, run, url]);

  // AUTH
  const login = useCallback(
    async (form: auth.LoginData) => {
      await auth.login(url, form).then((user) => {
        if (user) {
          setData({ user, posData: null, sessionId: null });
          posthog.identify(user.id.toString(), {
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
          });
          posthog.group("shop", id.toString(), { id, name });
        } else {
          setData(null);
        }
      });
    },
    [id, name, setData, url],
  );
  const logout = useCallback(() => {
    posthog.reset();
    setData(null);
    auth.logout();
  }, [setData]);

  // POS
  const setPos = useCallback(
    (posData: auth.POS | null) => {
      if (!posData) {
        setData((d) => {
          if (!d) return d;
          removeStoredItem(posKey(d.user));

          return { ...d, posData: null, sessionId: null };
        });
      } else {
        setData((d) => {
          if (!d) return d;
          saveStoredItem(posKey(d.user), posData.id.toString());
          posthog.group("pos", posData.id.toString(), { id: posData.id, name: posData.name });

          return { ...d, posData, sessionId: getStoredItem(sessionKey(d.user, posData)) };
        });
      }
    },
    [setData],
  );

  // SESSION
  const openSession = useCallback(
    async (form: cashDrawer.OpenInput) => {
      await cashDrawer.open(data?.posData?.id, form).then(({ sessionId }) => {
        setData((d) => {
          if (!d?.posData) return d;
          saveStoredItem(sessionKey(d.user, d.posData), sessionId);

          return { ...d, sessionId };
        });
      });
    },
    [data?.posData?.id, setData],
  );
  const closeSession = useCallback(
    async (form: cashDrawer.CloseInput) => {
      await cashDrawer.close(data?.posData?.id, form).then(() => {
        setData((d) => {
          if (!d || d.sessionId !== form.sessionId) return d;
          if (d.posData) removeStoredItem(sessionKey(d.user, d.posData));

          return { ...d, sessionId: null };
        });
      });
    },
    [data?.posData?.id, setData],
  );
  const setSession = useCallback(
    (sessionId: string | null) => {
      setData((d) => {
        if (!d) return d;
        if (d.posData) {
          const key = sessionKey(d.user, d.posData);

          sessionId ? saveStoredItem(key, sessionId) : removeStoredItem(key);
        }

        return { ...d, sessionId };
      });
    },
    [setData],
  );

  const value = useMemo(
    () => ({
      ...(data ?? { user: null, posData: null, sessionId: null }),
      hasPermissions: (permissions: auth.PERMISSIONS[] = []) =>
        permissions.every((permission) => data?.user.permissions.includes(permission)),
      isBackoffice: data?.posData?.id === "BACKOFFICE",
      login,
      logout,
      setPos,
      setSession,
      openSession,
      closeSession,
      openCashDrawer,
      setOpenCashDrawer,
    }),
    [closeSession, data, login, logout, openCashDrawer, openSession, setPos, setSession],
  );

  if (isLoading || isIdle) return <FullPageLoader />;
  if (isError && error) throw error;
  if (isSuccess) return <AuthContext.Provider value={value} {...props} />;
  throw new Error(`Unhandled status: ${status}`);
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) throw new Error(`useAuth must be used inside AuthProvider`);

  return context;
};

const usePos = () => {
  const { posData, isBackoffice } = useAuth();

  if (!posData) throw new Error("invalid pos");

  return {
    ...posData,
    isBackoffice,
    basePath: isBackoffice ? `api/backoffice` : `api/pos/${posData.id}`,
  };
};

export { AuthProvider, useAuth, usePos };
